import React, { useEffect, useState } from 'react';
import { Button, GoogleMapsAutocomplete, ToggleSwitch, SectionCard } from 'components';
import useApi from 'hooks/useApi';
import { Props } from 'types/modals';
import endpoints from 'constants/endpoints';
import Base from '../../base';

type Address = {
  lineOne: string;
  lineTwo?: string;
  city: string;
  state: string; // 2-letter code
  postalCode: string;
  latitude?: number;
  longitude?: number;
  formattedAddress?: string;
};

const SetEmployeeAddressModal = ({ closing, onClose, params }: Props) => {
  const api = useApi();
  const [resAddress, setResAddress] = useState<Address | null>(null);
  const [mailAddress, setMailAddress] = useState<Address | null>(null);
  const [resName, setResName] = useState('');
  const [mailName, setMailName] = useState('');
  const [sameResidence, setSameResidence] = useState(true);

  const handleSelectResidence = (value: any) => {
    setResName(value?.formattedAddress || value?.name || '');
    const { lineOne, lineTwo, city, state, postalCode } = value.address ? value.address : { ...value };
    setResAddress({
      lineOne,
      lineTwo: lineTwo || '',
      city,
      state,
      postalCode,
      latitude: value.coordinates?.lat,
      longitude: value.coordinates?.lng,
      formattedAddress: value.formattedAddress || undefined
    });
  };

  const handleSelectMailing = (value: any) => {
    setMailName(value?.formattedAddress || value?.name || '');
    const { lineOne, lineTwo, city, state, postalCode } = value.address ? value.address : { ...value };
    setMailAddress({
      lineOne,
      lineTwo: lineTwo || '',
      city,
      state,
      postalCode,
      latitude: value.coordinates?.lat,
      longitude: value.coordinates?.lng,
      formattedAddress: value.formattedAddress || undefined
    });
  };

  const handleSubmit = async () => {
    if (!resAddress) return;
    const payload: any = {
      residence: resAddress,
      sameResidence
    };
    if (!sameResidence && mailAddress) {
      payload.mailing = mailAddress;
    }
    await api.fetchData(endpoints.SET_EMPLOYEE_ADDRESS.get({ id: params?.id }), payload, true);
  };

  useEffect(() => {
    if (api.response?.success) {
      onClose(true);
    }
  }, [api.response]);

  return (
    <Base
      header="Set Employee Address"
      className="set-employee-address-modal"
      closing={closing}
      onClose={onClose}
      size="md"
      footer={
        <Button
          text="Save address"
          onClick={handleSubmit}
          disabled={!resAddress || (!sameResidence && !mailAddress) || api.isLoading}
          loading={api.isLoading}
        />
      }
    >
      <SectionCard>
        <GoogleMapsAutocomplete
          label="Search residential address"
          value={{
            name: resName,
            formattedAddress: resAddress?.formattedAddress || resName,
            address: {
              lineOne: resAddress?.lineOne || '',
              lineTwo: resAddress?.lineTwo || '',
              city: resAddress?.city || '',
              state: resAddress?.state || '',
              postalCode: resAddress?.postalCode || ''
            },
            coordinates: resAddress ? { lat: resAddress.latitude!, lng: resAddress.longitude! } : undefined
          }}
          onChange={() => {}}
          onLocationSelect={handleSelectResidence as any}
        />
      </SectionCard>

      <SectionCard>
        <div style={{ marginBottom: 8 }}>
          <p className="body-2" style={{ marginBottom: 8 }}>
            Mailing same as residential?
          </p>
          <ToggleSwitch value={sameResidence} onChange={setSameResidence} />
        </div>
        {!sameResidence && (
          <GoogleMapsAutocomplete
            label="Search mailing address"
            value={{
              name: mailName,
              formattedAddress: mailAddress?.formattedAddress || mailName,
              address: {
                lineOne: mailAddress?.lineOne || '',
                lineTwo: mailAddress?.lineTwo || '',
                city: mailAddress?.city || '',
                state: mailAddress?.state || '',
                postalCode: mailAddress?.postalCode || ''
              },
              coordinates: mailAddress ? { lat: mailAddress.latitude!, lng: mailAddress.longitude! } : undefined
            }}
            onChange={() => {}}
            onLocationSelect={handleSelectMailing as any}
          />
        )}
      </SectionCard>
    </Base>
  );
};

export default SetEmployeeAddressModal;
